import {
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  getDaysInMonth,
  getDaysInYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import i18n from '@/shared/i18n/i18n';
import { getDateLocale } from '@/shared/i18n/use-date-locale';
import { DateRange, TimelineConfig } from '../models/timeline-calendar.types';

const WEEK_STARTS_ON_MONDAY = 1 as const;

const YEARLY_CONFIG: TimelineConfig = {
  label: 'year',
  formatHeader: (date: Date) => format(date, i18n.t('dateFormat.yearOnly'), { locale: getDateLocale() }),
  getInterval: (start: Date, end: Date) =>
    eachYearOfInterval({ start: startOfYear(start), end: endOfYear(end) }),
  getDayCount: (date: Date) => getDaysInYear(date),
  getRangeStart: (dateRange: DateRange) => startOfYear(dateRange.start),
  getRangeEnd: (dateRange: DateRange) => endOfYear(dateRange.end),
};

const MONTHLY_CONFIG: TimelineConfig = {
  label: 'month',
  formatHeader: (date: Date) => format(date, i18n.t('dateFormat.yearMonth'), { locale: getDateLocale() }),
  getInterval: (start: Date, end: Date) =>
    eachMonthOfInterval({ start: startOfMonth(start), end: endOfMonth(end) }),
  getDayCount: (date: Date) => getDaysInMonth(date),
  getRangeStart: (dateRange: DateRange) => startOfMonth(dateRange.start),
  getRangeEnd: (dateRange: DateRange) => endOfMonth(dateRange.end),
};

const WEEKLY_CONFIG: TimelineConfig = {
  label: 'week',
  formatHeader: (date: Date) => format(date, i18n.t('dateFormat.weekLabel'), { locale: getDateLocale() }),
  getInterval: (start: Date, end: Date) =>
    eachWeekOfInterval(
      {
        start: startOfWeek(start, { weekStartsOn: WEEK_STARTS_ON_MONDAY }),
        end: endOfWeek(end, { weekStartsOn: WEEK_STARTS_ON_MONDAY }),
      },
      { weekStartsOn: WEEK_STARTS_ON_MONDAY },
    ),
  getDayCount: () => 7,
  getRangeStart: (dateRange: DateRange) =>
    startOfWeek(dateRange.start, { weekStartsOn: WEEK_STARTS_ON_MONDAY }),
  getRangeEnd: (dateRange: DateRange) =>
    endOfWeek(dateRange.end, { weekStartsOn: WEEK_STARTS_ON_MONDAY }),
};

const DAILY_CONFIG: TimelineConfig = {
  label: 'day',
  formatHeader: (date: Date) => format(date, i18n.t('dateFormat.dayLabel'), { locale: getDateLocale() }),
  getInterval: (start: Date, end: Date) =>
    eachDayOfInterval({ start: startOfDay(start), end: endOfDay(end) }),
  getDayCount: () => 1,
  getRangeStart: (dateRange: DateRange) => startOfDay(dateRange.start),
  getRangeEnd: (dateRange: DateRange) => endOfDay(dateRange.end),
};

export type TimelineScaleLabel = 'year' | 'month' | 'week' | 'day';

export const TIMELINE_SCALE_LABELS: TimelineScaleLabel[] = ['year', 'month', 'week', 'day'];

const CONFIG_BY_LABEL: Record<TimelineScaleLabel, TimelineConfig> = {
  year: YEARLY_CONFIG,
  month: MONTHLY_CONFIG,
  week: WEEKLY_CONFIG,
  day: DAILY_CONFIG,
};

/** 指定スケールに収まる代表的な日数スパン */
const SPAN_DAYS_BY_LABEL: Record<TimelineScaleLabel, number> = {
  year: 1095,  // ~3 years
  month: 180,   // ~6 months
  week: 30,    // ~1 month
  day: 7,     // ~1 week
};

export const getTimelineConfigByLabel = (label: TimelineScaleLabel): TimelineConfig =>
  CONFIG_BY_LABEL[label];

export const getSpanDaysByLabel = (label: TimelineScaleLabel): number =>
  SPAN_DAYS_BY_LABEL[label];

export const getTimelineConfigByRange = (dateRange: DateRange): TimelineConfig => {
  const spanMs = dateRange.end.getTime() - dateRange.start.getTime();
  const spanDays = spanMs / (24 * 60 * 60 * 1000);

  if (spanDays > 730) {
    return YEARLY_CONFIG;
  }
  if (spanDays > 60) {
    return MONTHLY_CONFIG;
  }
  if (spanDays > 14) {
    return WEEKLY_CONFIG;
  }
  return DAILY_CONFIG;
};
